import React, { useState, useEffect } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CollapsibleCard from "components/CollapsibleCard";
import { useStoreActions, useStoreState } from "easy-peasy";
import { formatCurrencyUSD } from "utils/CommonUtils";
import { get, post } from "requests";
import { useSubmissionStore } from "models/applications/SubmissionStore";
import EditableTable from "lib/Tables/EditableTable";
import MDInput from "components/MDInput";
import FormField from "layouts/pages/account/components/FormField";
import { CheckBox } from "@mui/icons-material";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";

interface EarlyOffProps {
  dealApplicationId: number;
  setComponentLoading?: (componentName: string, isLoading: boolean) => void;
}

const EarlyPayOffs: React.FC<EarlyOffProps> = ({ dealApplicationId, setComponentLoading }) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [dealOffersData, setDealOffersData] = useState<any>([]);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortedColumn, setSortedColumn] = useState<string | null>("amount");
  const [mode, setMode] = useState<string>("");
  const [earlyPayoffs, setEarlyPayOffs] = useState<any>([]);

  const setLoading = useStoreActions((actions: any) => actions.offerStore.setLoading);
  const [isEarlyPayOffAddtoContract, setIsEarlyPayOffAddtoContract] = useState(false);

  useEffect(() => {
    try {
      setComponentLoading("earlyPayoffs", true);
      getEarlyPayoffStatus();
      load();
    } finally {
      setComponentLoading("earlyPayoffs", false);
    }
  }, [dealApplicationId]);

  const columns = [
    {
      Header: "Number of Days",
      accessor: "withinNumberOfDays",
      allowSelection: false,
      input: "number",
      align: "left",
      width: "30%",
      required: false,
    },
    {
      Header: "Amount",
      accessor: "amount",
      input: "amount",
      align: "left",
      width: "30%",
      required: false,
      Cell: (row: any) => formatCurrencyUSD(row?.row?.amount),
    },
  ];

  const getEarlyPayoffStatus = async () => {
    const dealApplication = await get(`/getDealApplication/${dealApplicationId}`);
    setIsEarlyPayOffAddtoContract(dealApplication.isEarlyPayoffAddToContract);
  };

  const handleAddEarlyPayOffToContract = async (isEarlyPayoffAddToContract: any) => {
    try {
      await post("setIsEarlyPayoffAddToContract", {
        isEarlyPayoffAddToContract,
        dealApplicationId,
      });
      setIsEarlyPayOffAddtoContract(isEarlyPayoffAddToContract);
    } catch (err) {
      console.log(err);
    }
  };

  const load = async () => {
    const response = await get(`fetchEarlyPayoff/${dealApplicationId}`);
    const data = response.result;
    setEarlyPayOffs(data);
    if (data.length <= 0) {
      handleAddEarlyPayOffToContract(false);
    }
  };

  const handleSort = (columnKey: string) => {
    const isAsc = sortedColumn === columnKey && sortDirection === "asc";
    setSortedColumn(columnKey);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const handleInputChange = (value: any, field: string) => {
    setEditingRow((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectRow = (rowKey: number) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(rowKey)
        ? prevSelected.filter((key) => key !== rowKey)
        : [...prevSelected, rowKey]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === dealOffersData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(dealOffersData.map((row: any) => row.id));
    }
  };

  const handleEditPayOffs = (row: any | null) => {
    setIsEditing(true);
    setEditingRow(row ? { ...row } : {});
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setIsEditing(false);
    setMode("");
  };

  const deletePayoff = async (rowId: any) => {
    try {
      setLoading(true);
      const params = {
        dealApplicationId,
        recordKey: rowId,
      };
      await post("deleteEarlyPayoff", params);
      await load();
    } catch (error) {
      console.error("Error deleting record:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (row: any) => {
    if (!row || !row.amount || !row.withinNumberOfDays) {
      return;
    }

    try {
      setLoading(true);
      row["dealApplicationId"] = dealApplicationId;
      await post("addOrUpdateEarlyPayoff", row);
      await load();
    } catch (error) {
      console.error("Error saving early payoff:", error);
    } finally {
      setEditingRow(null);
      setIsEditing(false);
      setMode("");
      setLoading(false);
    }
  };

  const handleAddPayoff = () => {
    setMode("add");
    const payOff: any = {
      amount: "",
      withinNumberOfDays: "",
    };
    setEditingRow(payOff);
    setIsEditing(true);
  };

  const customButton = (
    <MDBox display="flex" gap={3}>
      <FormControlLabel
        control={
          <Checkbox
            checked={isEarlyPayOffAddtoContract}
            onChange={(event) => handleAddEarlyPayOffToContract(event?.target?.checked)}
          />
        }
        label={
          <Typography fontSize="1rem" fontWeight={600} color={"grey"}>
            Add to Contract
          </Typography>
        }
        sx={{ whiteSpace: "nowrap", marginRight: 0, display: "flex", alignItems: "center" }}
      />
      <MDButton
        color="primary"
        variant="contained"
        size="medium"
        sx={{ textTransform: "none" }}
        onClick={handleAddPayoff}
      >
        + Add
      </MDButton>
    </MDBox>
  );

  const renderTotal = (key: any) => {
    let total = 0;
    if (editingRow && editingRow[key]) {
      const editingAmount = parseFloat(editingRow[key]);
      if (!isNaN(editingAmount)) {
        total += editingAmount;
      }
    }
    earlyPayoffs
      .filter((row: any) => !editingRow || editingRow.id !== row.id)
      .forEach((row: any) => {
        const rowAmount = parseFloat(row[key]);
        if (!isNaN(rowAmount)) {
          total += rowAmount;
        }
      });

    return key.includes("amount") ? formatCurrencyUSD(total.toFixed(2)) : total.toFixed(2);
  };

  const renderTable = () => {
    return (
      <EditableTable
        rowKey={"recordKey"}
        columns={columns}
        data={earlyPayoffs}
        onDelete={deletePayoff}
        onEdit={handleEditPayOffs}
        onSave={onSave}
        onCancel={handleCancelEdit}
        editingRow={editingRow}
        isEditing={isEditing}
        onInputChange={handleInputChange}
        selectedRows={selectedRows}
        onSelectRow={handleSelectRow}
        onSelectAll={handleSelectAll}
        onSort={handleSort}
        getRowStyle={() => "regular"}
        isActionsDisabled={null}
        renderTotal={renderTotal}
        totalColumn={["amount"]}
      />
    );
  };

  return (
    <CollapsibleCard header={"Early Payoffs"} customButton={customButton}>
      <MDBox paddingRight={3}>{renderTable()}</MDBox>
    </CollapsibleCard>
  );
};

export default EarlyPayOffs;
